import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Users, FileText, DollarSign, ArrowRight, Handshake, Copy, Check, Mail, MessageSquare, Send, Twitter, Linkedin, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";

const STEPS = [
  { icon: Users, title: "Refer a borrower or deal", body: "Send an investor or business-purpose loan scenario to BullsEye Capital using your unique link." },
  { icon: FileText, title: "We handle the rest", body: "We manage lender matching, packaging, and processing from application to closing." },
  { icon: DollarSign, title: "You get paid", body: "You earn your share of our fee when the loan closes and funds." },
];

const TIERS = [
  { name: "Standard Partner", payout: "15%", range: "1–9 funded deals", featured: false },
  { name: "Mid-Tier Partner", payout: "20%", range: "10–24 funded deals", featured: true },
  { name: "Strategic Partner", payout: "25%", range: "25+ funded deals", featured: false },
];

const EXAMPLES = [
  ["$150,000", "$2,625", "$393.75", "$525", "$656.25"],
  ["$250,000", "$4,375", "$656.25", "$875", "$1,093.75"],
  ["$400,000", "$7,000", "$1,050", "$1,400", "$1,750"],
  ["$500,000", "$8,750", "$1,312.50", "$1,750", "$2,187.50"],
];

function ShareProgram() {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/referral-program`;
  const subj = "Become a BullsEye Capital Referral Partner";
  const msg = `Join the BullsEye Capital Referral Program and earn on every real estate deal you send. Learn more & sign up: ${url}`;
  const copy = () => { navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1800); };
  const nativeShare = async () => {
    if (navigator.share) { try { await navigator.share({ title: subj, text: "Become a BullsEye Capital Referral Partner", url }); } catch (e) { /* dismissed */ } }
    else copy();
  };
  const targets = [
    { key: "email", Icon: Mail, label: "Email", href: `mailto:?subject=${encodeURIComponent(subj)}&body=${encodeURIComponent(msg)}` },
    { key: "sms", Icon: MessageSquare, label: "Text", href: `sms:?&body=${encodeURIComponent(msg)}` },
    { key: "whatsapp", Icon: Send, label: "WhatsApp", href: `https://wa.me/?text=${encodeURIComponent(msg)}` },
    { key: "x", Icon: Twitter, label: "X", href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(subj)}&url=${encodeURIComponent(url)}` },
    { key: "linkedin", Icon: Linkedin, label: "LinkedIn", href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}` },
  ];
  return (
    <div className="mt-10 flex flex-col items-center gap-3" data-testid="share-program">
      <span className="text-xs uppercase tracking-[0.2em] text-faint">Share the referral program</span>
      <div className="flex flex-wrap items-center justify-center gap-2">
        <button onClick={copy} data-testid="share-program-copy" className="inline-flex items-center gap-1.5 rounded-full border border-gold/50 bg-gold/10 text-gold px-4 h-9 text-sm hover:bg-gold/20 transition-colors">
          {copied ? <><Check className="w-4 h-4" /> Copied</> : <><Copy className="w-4 h-4" /> Copy link</>}
        </button>
        {targets.map((t) => (
          <a key={t.key} href={t.href} target="_blank" rel="noreferrer" title={`Share via ${t.label}`} data-testid={`share-program-${t.key}`}
            className="inline-flex items-center gap-1.5 rounded-full border border-edge hover:border-gold/60 hover:bg-gold/10 text-white/80 hover:text-gold px-3 h-9 text-sm transition-colors">
            <t.Icon className="w-4 h-4" /> <span className="hidden sm:inline">{t.label}</span>
          </a>
        ))}
        <button onClick={nativeShare} data-testid="share-program-native" title="More share options" className="inline-flex items-center gap-1.5 rounded-full border border-edge hover:border-gold/60 hover:bg-gold/10 text-white/80 hover:text-gold px-3 h-9 text-sm transition-colors">
          <Share2 className="w-4 h-4" /> <span className="hidden sm:inline">More</span>
        </button>
      </div>
    </div>
  );
}

export default function ReferralProgram() {
  return (
    <div className="bg-obsidian" data-testid="referral-program-page">
      {/* Hero */}
      <section className="relative border-b border-edge">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-20 text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-gold">
            <Handshake className="w-4 h-4" /> People · Deals · Growth Together
          </div>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl text-white">
            BullsEye Capital <span className="italic text-gold">Referral Program</span>
          </h1>
          <p className="mt-5 text-dim text-base max-w-2xl mx-auto">
            Referral compensation for investor and business-purpose loan transactions. Real people. Real capital. Real opportunity.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/register"><Button className="btn-gold rounded-full h-12 px-7 gap-2" data-testid="referral-cta-register">Get Your Referral Link <ArrowRight className="w-4 h-4" /></Button></Link>
            <Link to="/portal/referrals"><Button variant="outline" className="btn-outline-glass rounded-full h-12 px-7">Partner Sign In</Button></Link>
          </div>
          <ShareProgram />
        </div>
      </section>

      {/* How it works */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-16">
        <h2 className="eyebrow text-center">How It Works</h2>
        <div className="mt-8 grid md:grid-cols-3 gap-5">
          {STEPS.map((s, i) => (
            <div key={i} className="card-rich rounded-2xl p-6">
              <div className="icon-chip"><s.icon className="w-5 h-5 text-gold" /></div>
              <div className="mt-4 text-sm text-gold font-semibold">Step {i + 1}</div>
              <h3 className="mt-1 text-lg text-white font-semibold">{s.title}</h3>
              <p className="mt-2 text-sm text-dim leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Fee */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 pb-4">
        <div className="card-rich rounded-2xl p-8 flex flex-col sm:flex-row items-center gap-6 text-center sm:text-left">
          <div className="icon-chip shrink-0"><DollarSign className="w-6 h-6 text-gold" /></div>
          <div>
            <div className="eyebrow">Our Fee</div>
            <p className="mt-1 text-2xl sm:text-3xl font-display text-white">BullsEye Capital fee: <span className="text-gold">1.75%</span> of the funded loan amount</p>
            <p className="text-dim text-sm mt-1">Your payout is a share of this fee based on your partner tier.</p>
          </div>
        </div>
      </section>

      {/* Tiers */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-16">
        <h2 className="eyebrow text-center">Referral Tiers</h2>
        <div className="mt-8 grid md:grid-cols-3 gap-5">
          {TIERS.map((t) => (
            <div key={t.name} className={`rounded-2xl p-6 border ${t.featured ? "border-gold bg-gold/[0.07]" : "border-edge card-surface"}`}>
              {t.featured && <div className="text-[11px] uppercase tracking-wide text-gold mb-2">Most partners</div>}
              <h3 className="text-lg text-white font-semibold">{t.name}</h3>
              <div className="mt-3 text-4xl font-display text-gold">{t.payout}</div>
              <p className="text-dim text-sm mt-1">of BullsEye Capital's collected fee</p>
              <div className="mt-4 pt-4 border-t border-edge text-sm text-white/80">Unlocked at <strong className="text-white">{t.range}</strong></div>
            </div>
          ))}
        </div>
        <p className="mt-4 text-center text-sm text-dim">Your tier is based on the number of deals you've closed and funded with BullsEye Capital, and updates automatically as you grow.</p>
      </section>

      {/* Payout examples */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 pb-16">
        <h2 className="eyebrow text-center">Payout Examples</h2>
        <div className="mt-8 overflow-x-auto rounded-2xl border border-edge">
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="text-left bg-surface/60 border-b border-edge text-faint">
                <th className="py-3 px-4 font-semibold">Loan Amount</th>
                <th className="py-3 px-4 font-semibold">BullsEye Fee</th>
                <th className="py-3 px-4 font-semibold">15% Payout</th>
                <th className="py-3 px-4 font-semibold">20% Payout</th>
                <th className="py-3 px-4 font-semibold">25% Payout</th>
              </tr>
            </thead>
            <tbody>
              {EXAMPLES.map((row, i) => (
                <tr key={i} className="border-b border-edge/50">
                  <td className="py-3 px-4 text-white font-semibold">{row[0]}</td>
                  <td className="py-3 px-4 text-white/80">{row[1]}</td>
                  <td className="py-3 px-4 text-white/80">{row[2]}</td>
                  <td className="py-3 px-4 text-gold font-semibold">{row[3]}</td>
                  <td className="py-3 px-4 text-goldhi font-semibold">{row[4]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-edge">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-16 text-center">
          <h2 className="font-display text-3xl sm:text-4xl text-white">Start earning on every deal you send</h2>
          <p className="mt-3 text-dim">Create your free partner account and get your referral link in seconds.</p>
          <Link to="/register"><Button className="btn-gold rounded-full h-12 px-8 mt-6 gap-2">Become a Referral Partner <ArrowRight className="w-4 h-4" /></Button></Link>
          <p className="mt-8 text-xs text-faint max-w-xl mx-auto">Referral compensation is paid on closed and funded transactions only. Final terms are subject to referral agreement and applicable law.</p>
        </div>
      </section>
    </div>
  );
}
